import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { Container, Grid, Typography, Box, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import { months, sampleYear } from "../../../constants";
import { STATUS } from "../../../redux/constants";
import { addWaterSupplyValidation } from "./schema";
import {
  addWaterSupply,
  getEmissionInputFormat,
  resetAddCombustionStatus,
} from "../../../redux/actions";
import CeroAutoComplete from "../../../components/CeroAutoComplete";
import CeroButton from "../../../components/CeroButton";
import CeroSelect from "../../../components/CeroSelect";
import CeroInput from "../../../components/CeroInput";
import CeroInfoPair from "../../../components/CeroInfoPair";
import useStyles from "./styles";
import { useNavigate } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileUpload from "../FileUpload";

const AddWaterSupplyform = (props) => {
  const { onCancel } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const isCalculateDone = useSelector(
    (state) => state.emission.addWaterSupply.isCalculateDone
  );
  const facilitiesData = useSelector(
    (state) => state.listings.listFacilities.data
  );
  const addEmissionData = useSelector((state) => state.emission.addWaterSupply);
  const emissionInputs = useSelector(
    (state) => state.emission.emissionInputs.data
  );

  const facilitiesList = facilitiesData.map((item) => ({
    key: item.id,
    value: item.name,
  }));
  const fuelUnits =
    emissionInputs &&
    (emissionInputs.units || []).map((item) => ({
      key: item.name,
      value: item.name,
    }));
  const waterDestinationList =
    emissionInputs &&
    (emissionInputs.water_sources || []).map((item) => ({
      key: item.id,
      value: item.name,
    }));
  const stressTypeList =
    emissionInputs &&
    (emissionInputs.water_source_stress_types || []).map((item) => ({
      key: item.id,
      value: item.name,
    }));
  const destinationTypeList =
    emissionInputs &&
    (emissionInputs.water_source_types || []).map((item) => ({
      key: item.id,
      value: item.name,
    }));
  const yearList = sampleYear().map((item) => ({
    id: item.key,
    label: item.value,
  }));
  const currentYear = new Date().getFullYear().toString();
  const [selectedFile, setSelectedFile] = useState(null);
  const treatmentLevelList = ["Primary", "Secondary", "Tertiary"].map(
    (item, index) => ({ key: index + 1, value: item })
  );

  // Formik initialValues: keep the whole option object
  const initialYearOption =
    yearList.find((o) => String(o.id) === String(new Date().getFullYear())) ??
    yearList[0];

  const formik = useFormik({
    initialValues: {
      facility: "",
      year: initialYearOption,
      month: "",
      destination: "",
      stressType: "",
      destinationType: "",
      treatmentRequired: "",
      treatmentLevel: "",
      usage: null,
      fuelUnit: "",
    },
    validationSchema: addWaterSupplyValidation,
    onSubmit: () => {},
  });

  useEffect(() => {
    dispatch(getEmissionInputFormat("water_supply"));
    return () => {
      dispatch(resetAddCombustionStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    if (addEmissionData.status === STATUS.ERROR) {
      enqueueSnackbar(
        addEmissionData.message.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      // Keep this if it only resets the emission slice; it shouldn't touch Formik state.
      dispatch(resetAddCombustionStatus());
    }
  }, [addEmissionData.status, dispatch, enqueueSnackbar, onCancel]);

  useEffect(() => {
    if (
      addEmissionData.status === STATUS.SUCCESS &&
      !addEmissionData.isCalculateDone
    ) {
      enqueueSnackbar("Water supply added successfully", {
        variant: "success",
      });
      dispatch(resetAddCombustionStatus());
      onCancel();
    }
  }, [
    addEmissionData.status,
    addEmissionData.isCalculateDone,
    dispatch,
    enqueueSnackbar,
    onCancel,
  ]);

  useEffect(() => {
    if (!formik.values.treatmentRequired) {
      formik.setFieldValue("treatmentLevel", "");
    }
  }, [formik.values.treatmentRequired]);

  const handleFileUpload = (file) => {
    setSelectedFile(file);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
  };

  const toNumber = (v) =>
    v === "" || v === null || v === undefined ? undefined : Number(v);

  const onCalculate = () => {
    const requestData = {
      facility_id: formik.values.facility, // id string
      year: toNumber(formik.values.year?.id), // number
      month: toNumber(formik.values.month), // 1–12
      water_source_id: formik.values.destination, // id string
      water_source_stress_type_id: formik.values.stressType, // id string
      water_source_type_id: formik.values.destinationType, // id string
      usage: toNumber(formik.values.usage), // number
      usage_unit: formik.values.fuelUnit, // string
      file: selectedFile, // optional
      save: false,
    };
    dispatch(addWaterSupply(requestData));
  };

  const onAddWaterSupplyData = () => {
    const requestData = {
      facility_id: formik.values.facility, // id string
      year: toNumber(formik.values.year?.id), // number
      month: toNumber(formik.values.month), // 1–12
      water_source_id: formik.values.destination, // id string
      water_source_stress_type_id: formik.values.stressType, // id string
      water_source_type_id: formik.values.destinationType, // id string
      usage: toNumber(formik.values.usage), // number
      usage_unit: formik.values.fuelUnit, // string
      file: selectedFile, // optional
      save: true,
    };
    sessionStorage.setItem("year", String(requestData.year ?? ""));
    dispatch(addWaterSupply(requestData));
  };

  return (
    <>
      <div
        className={classes.backContainer}
        onClick={() => {
          navigate(-1);
        }}
      >
        <ArrowBackIosIcon />
        Back
      </div>
      <Container className={classes.container}>
        <Box className={classes.innerContainer}>
          <Typography className={classes.title} variant="h6" component="div">
            Add Water Supply
          </Typography>
          <Box className={classes.topContainer}>
            <Grid
              container
              direction="row"
              wrap="nowrap"
              justifyContent="space-between"
              spacing={4}
            >
              <Grid item container direction="column" xs={4}>
                <CeroSelect
                  required
                  id="facility"
                  name="facility"
                  label="Facility"
                  fullWidth
                  options={facilitiesList}
                  selectedValue={formik.values.facility}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.facility && formik.errors.facility}
                />
                <CeroSelect
                  required
                  id="month"
                  name="month"
                  label="Month"
                  fullWidth
                  options={months}
                  selectedValue={formik.values.month}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.month && formik.errors.month}
                />
                <CeroSelect
                  required
                  id="water_sources"
                  name="destination"
                  label="Water Sources"
                  fullWidth
                  options={waterDestinationList}
                  selectedValue={formik.values.destination}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.destination && formik.errors.destination
                  }
                />

                <CeroInput
                  required
                  id="usage"
                  name="usage"
                  label="Amount of Water Usage"
                  value={formik.values.usage}
                  fullWidth
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.usage && formik.errors.usage}
                  type="number"
                />
              </Grid>
              <Grid
                item
                container
                direction="column"
                xs={1}
                style={{ position: "relative" }}
              >
                <span className={classes.treatementRequired}></span>
              </Grid>
              <Grid item container direction={"column"} xs={4}>
                <CeroSelect
                  required
                  id="water_source_stress_types"
                  name="stressType"
                  label="Water Stress type"
                  fullWidth
                  options={stressTypeList}
                  selectedValue={formik.values.stressType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.stressType && formik.errors.stressType}
                />
                <CeroAutoComplete
                  id="year"
                  label="Assessment Year"
                  options={yearList} // [{ id, label }]
                  value={formik.values.year} // <- keep the whole object in Formik
                  onChange={(e, option) => formik.setFieldValue("year", option)}
                  onBlur={formik.handleBlur}
                  error={formik.touched.year && formik.errors.year}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                />
                <CeroSelect
                  required
                  id="water_source_types"
                  name="destinationType"
                  label="Water Source Type"
                  fullWidth
                  options={destinationTypeList}
                  selectedValue={formik.values.destinationType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.destinationType &&
                    formik.errors.destinationType
                  }
                  classes={{
                    input: classes.input,
                    container: classes.selectContainer,
                    paperRoot: classes.paperRoot,
                  }}
                />

                <CeroSelect
                  required
                  id="fuelUnit"
                  name="fuelUnit"
                  label="Unit"
                  fullWidth
                  options={fuelUnits}
                  selectedValue={formik.values.fuelUnit}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.fuelUnit && formik.errors.fuelUnit}
                />
              </Grid>
              <Grid item container direction="column" md={6} xs={1}>
                <span className={classes.info}>
                  {/* <Tooltip
                    title={info()}
                    classes={{ popper: classes.tooltip }}
                    arrow
                    placement="right-start"
                  >
                    <InfoOutlinedIcon className={classes.infoIcon} />
                  </Tooltip> */}
                </span>
              </Grid>
            </Grid>
            <CeroButton
              buttonText="Calculate"
              className={classes.buttonPrimary}
              onClick={onCalculate}
              disabled={!formik.dirty || !formik.isValid}
            />
            <CeroButton
              buttonText="Clear"
              variant="outlined"
              className={classes.buttonSecondary}
              onClick={formik.resetForm}
            />

            {isCalculateDone && addEmissionData?.data && (
              <Box className={classes.bottomContainer}>
                <Typography
                  variant="h6"
                  component="h6"
                  className={classes.previewTitle}
                >
                  Emission Preview
                </Typography>

                {(() => {
                  // works whether the reducer kept the wrapper or unwrapped it
                  const emission =
                    addEmissionData.data?.emission ?? addEmissionData.data;

                  return (
                    <Grid
                      container
                      direction="row"
                      wrap="nowrap"
                      justifyContent="space-between"
                      spacing={8}
                    >
                      <Grid item container direction="column" xs={12} md={6}>
                        <CeroInfoPair
                          title="Usage"
                          value={
                            <span style={{ textTransform: "none" }}>
                              {emission?.usage ?? ""}{" "}
                              {emission?.usage_unit ?? ""}
                            </span>
                          }
                        />
                        <CeroInfoPair
                          title={
                            <>
                              CO<sub>2</sub>e
                            </>
                          }
                          value={
                            <span style={{ textTransform: "none" }}>
                              {emission?.co2e ?? ""}{" "}
                              {emission?.emission_unit ?? ""}
                            </span>
                          }
                        />
                      </Grid>
                    </Grid>
                  );
                })()}
              </Box>
            )}
          </Box>
          <FileUpload
            onFileUpload={handleFileUpload}
            onFileRemove={handleFileRemove}
          />
        </Box>
        <Box className={classes.buttonContainer}>
          <CeroButton
            buttonText="Add Data"
            disabled={
              !isCalculateDone || addEmissionData.status === STATUS.RUNNING
            }
            className={classes.buttonPrimary}
            onClick={() => onAddWaterSupplyData(formik.values)}
          />
        </Box>
      </Container>
    </>
  );
};

export default AddWaterSupplyform;
